import React, {
  useState,
  FC,
  createContext,
  useContext
} from 'react';
import styled, { AnyStyledComponent } from 'styled-components';

import {
  TabPanelProps,
  TabsProps,
  TabObject,
  ContextType,
  TabsContextProps,
  TabListProps,
} from './tabs.types';
import { default as ButtonGroupPattern } from '../ButtonGroup/buttonGroup';
import { ButtonShapes, ButtonStyles, ButtonSizes } from '../Button/button.types';

const fixTabName = (name: String) => name.replace(/ /g, '_').toLowerCase();
const Context = createContext<ContextType | null>(null);

const TabsContext: FC<TabsContextProps> = ({
  initialActiveTab = '',
  children,
}) => {

  const [
    activeTab,
    setActiveTab,
  ] = useState(initialActiveTab);

  const tabProviderValue = {
    activeTab,
    setActiveTab,
  };

  return (
    <Context.Provider value={tabProviderValue}>
      {children}
    </Context.Provider>
  );

};

const useTabsContext = () => {
  const context = useContext(Context);
  if (context === null) {
    throw new Error('useTabsContext must be used within a TabsContext Provider');
  }
  return context;
}

const ButtonGroup: AnyStyledComponent = styled(ButtonGroupPattern)`
  padding-bottom: 12px;
`;

const DEFAULT_DATA_ATTR = { 'data-testid': 'tab-list' };

const TabList: FC<TabListProps> = ({
  buttonProps = {},
  buttonSize = ButtonSizes.MEDIUM,
  dataAttr = {},
  list = [],
}) => {
  const tabContext = useTabsContext();
  const tabs = list.map((tabProps, i) => {
    const DEFAULT_DATA_ATTR = { 'data-testid': `tab-button--${i}`};
    const combinedDataAttr = {...DEFAULT_DATA_ATTR, ...(tabProps.dataAttr ?? {})};
    const handlers = {
      ...tabProps.handlers,
      onClick: (event: MouseEvent) => {
        tabContext?.setActiveTab(tabProps.name);

        if (tabProps.handlers?.onClick) {
          tabProps.handlers.onClick(event);
        }
      }
    };

    return {
      ...tabProps,
      dataAttr: combinedDataAttr,
      handlers: handlers,
      hasHover: false,
      key: tabProps.name,
      label: tabProps.name,
      shape: ButtonShapes.TAB,
      size: buttonSize,
      ...buttonProps,
    };
  });
  const activeTabIndex = list.findIndex((tab) => tab.name === tabContext?.activeTab);
  const combinedDataAttr = {...DEFAULT_DATA_ATTR, ...dataAttr};

  return (
    <ButtonGroup
      data={tabs}
      dataAttr={combinedDataAttr}
      selected={activeTabIndex}
      selectedStyleName={ButtonStyles.SELECTED_TAB}
    />
  );

};

const TabPanel: FC<TabPanelProps> = ({
  name = '',
  children,
  ...restProps
}: TabPanelProps) => {

  const tabContext = useTabsContext();
  return (
    (tabContext && tabContext.activeTab) === name ?
      (
        <div
          id={`tabpanel_${fixTabName(name)}`}
          aria-labelledby={`tab_${fixTabName(name)}`}
          {...restProps}
        >
          {children}
        </div>
      ) : null
  );

};


const Tabs: FC<TabsProps> = ({
  buttonProps = {},
  buttonSize = ButtonSizes.MEDIUM,
  initialActiveTab = '',
  list = [],
  listDataAttr,
}: TabsProps) => {

  return (
    <TabsContext initialActiveTab={initialActiveTab}>
      <TabList
        buttonProps={buttonProps}
        buttonSize={buttonSize}
        dataAttr={listDataAttr}
        list={list}
      />
      {list.map((tab: TabObject) => (
        <TabPanel key={tab.name} name={tab.name}>
          {tab.children}
        </TabPanel>
      ))}
    </TabsContext>
  );
};

export {
  TabPanel,
  Tabs,
  TabsContext,
  TabList,
  useTabsContext,
};
